import React, { useState } from 'react';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const JourneyTour = ({ onClose, dontShowAgain, setDontShowAgain }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem('journey_wizard_dont_show_tour', 'true');
    }
    onClose();
  };

  const slides = [
    {
      title: "Why This Matters: The Problems Most Products Face",
      content: (
        <div className="space-y-4">
          <p className="text-lg font-semibold text-orange-700">Most products fail because they skip the customer journey.</p>
          <p className="text-slate-700">Without a clear understanding of your customer's path, you're building in the dark. Here's what happens:</p>
          <ul className="list-disc list-inside space-y-3 text-slate-700 ml-2">
            <li><strong>Features built without customer context</strong> — You add functionality that doesn't solve real pain points</li>
            <li><strong>Vague value propositions</strong> — Prospects can't understand why they should choose you</li>
            <li><strong>Unclear competitive position</strong> — You can't articulate what makes you different</li>
            <li><strong>Scattered strategy</strong> — Your team isn't aligned on who you're serving and how</li>
            <li><strong>Wasted time and money</strong> — Building the wrong things or marketing to the wrong people</li>
          </ul>
          <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mt-4">
            <p className="text-sm font-semibold text-orange-900">The earlier you map the journey, the faster you find product-market fit.</p>
          </div>
        </div>
      )
    },
    {
      title: "What This Wizard Does",
      content: (
        <div className="space-y-4">
          <p className="text-lg">Turn your product ideas into a clear Customer Journey Map and story.</p>
          <ul className="list-disc list-inside space-y-2 text-slate-700">
            <li>Answer a few guided questions</li>
            <li>We generate journey maps, story arcs, and interactive stories</li>
            <li>Export to Visual Story Planner or download as files</li>
          </ul>
        </div>
      )
    },
    {
      title: "Who This Is For",
      content: (
        <ul className="list-disc list-inside space-y-3 text-slate-700">
          <li><strong>Founders and business owners</strong> who need to explain their product clearly</li>
          <li><strong>Product managers and consultants</strong> who need structured Customer Journey Maps</li>
          <li><strong>Anyone</strong> who wants to turn features into compelling customer stories</li>
        </ul>
      )
    },
    {
      title: "What You Get",
      content: (
        <ul className="list-disc list-inside space-y-3 text-slate-700">
          <li>A structured <strong>Customer Journey Map</strong></li>
          <li>A clear <strong>story arc</strong> (before → conflict → solution → transformation)</li>
          <li>A <strong>feature–value–evidence matrix</strong></li>
          <li>A competitive advantage overview</li>
          <li>Auto-generated <strong>Twee story skeleton</strong> for Visual Story Planner</li>
        </ul>
      )
    },
    {
      title: "Save As You Go, Resume Anytime",
      content: (
        <div className="space-y-4">
          <p className="text-lg font-semibold text-green-700">Nothing is locked into one session!</p>
          <ul className="list-disc list-inside space-y-3 text-slate-700">
            <li>At every step, you can <strong>save your work</strong> and download it as a file</li>
            <li>Later, <strong>upload that file</strong> to continue where you left off</li>
            <li>Your progress is also saved in your browser automatically</li>
          </ul>
        </div>
      )
    },
    {
      title: "AI Assistant Available",
      content: (
        <div className="space-y-4">
          <p className="text-lg">🤖 <strong>Get AI help at every step!</strong></p>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="font-semibold text-orange-900 mb-2">How it works:</p>
            <ol className="list-decimal list-inside space-y-2 text-slate-700">
              <li>Click any text field to select it</li>
              <li>Open the AI Assistant at the bottom of the screen</li>
              <li>Ask for help (e.g., "Help me write a persona for B2B software")</li>
              <li>Click "Use This" to fill the field automatically</li>
            </ol>
          </div>
          <p className="text-sm text-slate-600 italic">The AI speeds up your writing and provides expert suggestions!</p>
        </div>
      )
    },
    {
      title: "Minimalist, Efficient Workflow",
      content: (
        <div className="space-y-4">
          <p className="text-lg">You focus on answers; the tool builds the structure.</p>
          <ul className="list-disc list-inside space-y-3 text-slate-700">
            <li>Complete the wizard in <strong>15-20 minutes</strong></li>
            <li>You can stop after any step and still export useful outputs</li>
            <li>No complex terminology - everything in plain language</li>
            <li>Navigate back and forth between steps freely</li>
          </ul>
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="font-semibold text-blue-900">Ready to start?</p>
            <p className="text-sm text-blue-700 mt-1">Click "Start Wizard" to begin your customer journey!</p>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1));
  const prevSlide = () => setCurrentSlide(Math.max(0, currentSlide - 1));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-4 sm:p-6 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold truncate">Customer Journey Wizard Tour</h2>
            <p className="text-xs sm:text-sm text-orange-100 mt-1">Slide {currentSlide + 1} of {slides.length}</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-orange-100 ml-2 flex-shrink-0">
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 mb-4 sm:mb-6">{slides[currentSlide].title}</h3>
          <div className="text-sm sm:text-base">{slides[currentSlide].content}</div>
        </div>

        {/* Footer Navigation */}
        <div className="border-t border-slate-200 p-3 sm:p-4 md:p-6 bg-slate-50 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="outline"
              onClick={prevSlide}
              disabled={currentSlide === 0}
              size="sm"
              className="flex-shrink-0"
            >
              <ChevronLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Previous</span>
            </Button>

            <div className="flex gap-1 sm:gap-2">
              {slides.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${
                    idx === currentSlide ? 'bg-orange-500' : 'bg-slate-300'
                  }`}
                />
              ))}
            </div>

            {currentSlide < slides.length - 1 ? (
              <Button onClick={nextSlide} size="sm" className="flex-shrink-0">
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="h-4 w-4 sm:ml-2" />
              </Button>
            ) : (
              <Button onClick={handleClose} size="sm" className="bg-green-600 hover:bg-green-700 flex-shrink-0">
                <span className="text-xs sm:text-sm">Start</span>
              </Button>
            )}
          </div>
          
          {/* Don't Show Again Checkbox */}
          <div className="flex items-center justify-center pt-2 border-t border-slate-300">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span>Don't show this tour again</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyTour;
